<script setup lang="ts">
import { Eye, CheckCircle, Printer } from "lucide-vue-next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { formatDate } from "@lib/utils/formatDate";
import { storeOrdersApiClient } from "@api-client/store_orders";
import type { ResType } from "@api-client/typeUtils";
import { isStoreOrderAllowedToAccept, STORE_ORDER_STATUS_OBJ } from "@lib/types/storeOrderStatus";
import { printOrder } from "./printHTML";
import { showOrderDialog } from "./OrderDialogShow";
import { handleAccept, isAcceptingOrder } from "./handleAccept";

const ApiPath = storeOrdersApiClient[":storeId"].get_by_order_ids.$post;
type OrderType = ResType<typeof ApiPath>["data"];

defineProps<{
    order: OrderType[number];
}>();

const browserLocale = window.navigator.language;

const IS_DEV = import.meta.env.DEV;

</script>
<template>
    <Card class="p-2">
        <CardContent class="p-0">
            <div class="flex flex-col md:flex-row justify-between gap-4">
                <!-- Order Info -->
                <div @click="showOrderDialog(order)"
                     class="w-full cursor-pointer">
                    <div class="space-y-2 flex-grow hover:bg-secondary/50 transition-colors p-2 rounded-md ">
                        <div class="flex flex-wrap items-center gap-2">
                            <h3 class="text-lg font-bold">#{{ order.shortOtp || 'N/A' }}</h3>
                            <Badge
                                   :variant="`${order.orderStatus === STORE_ORDER_STATUS_OBJ.CANCELLED ? 'destructive' : 'secondary'}`">
                                {{ order.orderStatus }}
                            </Badge>
                            <Badge variant="secondary"
                                   class="capitalize">
                                {{ order.orderType || 'N/A' }}
                            </Badge>
                            <div v-if="order.scheduled_at_ts">
                                <Badge variant="secondary"
                                       class="capitalize text-xs">
                                    SCHEDULED
                                </Badge>
                            </div>
                            <span v-if="order.isCancelled"
                                  class="text-xs border border-destructive-foreground p-1 rounded-md inline-block m-1 bg-destructive text-destructive-foreground underline">
                                Order CANCELLED
                            </span>
                        </div>
                        <div class="text-left text-xs text-muted-foreground">
                            <p>
                                {{
                                    order.deliveryAddressJSON?.shippingName ||
                                    order.pickupNameSurname ||
                                    order.userName ||
                                    order.userEmail || 'unknown'
                                }}
                            </p>
                            <p>
                                {{ formatDate(order.order_placed_at_ts, undefined, true) }}
                            </p>
                            <p v-if="order.order_placed_at_ts !== order.draft_create_at_ts">
                                Draft: {{ formatDate(order.draft_create_at_ts, undefined, true) }}
                            </p>
                            <p v-if="order.scheduled_at_ts">
                                Scheduled: {{ formatDate(order.scheduled_at_ts!, undefined, true) }}
                            </p>
                            <p>
                                {{ order.storeName }}
                                ({{ order.storeAddress1 }})
                            </p>
                        </div>
                    </div>
                </div>
                <!-- Actions -->
                <div class="flex flex-wrap gap-2 items-center">
                    <div class="flex gap-2"
                         v-if="isStoreOrderAllowedToAccept(order.orderStatus)">
                        <Button size="sm"
                                class="animate__tada"
                                :disabled="isAcceptingOrder"
                                @click="handleAccept(order.storeOrderId, order.storeId)">
                            <CheckCircle class="mr-1" />
                            Accept
                        </Button>
                    </div>

                    <Button variant="secondary"
                            size="sm"
                            @click="printOrder(order, browserLocale)">
                        <Printer class="mr-1" />
                        Print
                    </Button>

                    <Button variant="outline"
                            @click="showOrderDialog(order)"
                            size="sm">
                        Show
                    </Button>

                    <Popover v-if="IS_DEV">
                        <PopoverTrigger as-child>
                            <Button variant="ghost"
                                    size="sm">
                                <Eye class="h-4 w-4" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent class="max-w-xl w-full max-h-96 overflow-y-auto">
                            <pre class="text-xs">{{ order }}</pre>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>
        </CardContent>
    </Card>
</template>